import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { QRCodeSVG } from 'qrcode.react';
import { io, Socket } from 'socket.io-client';
import { mintStaffQrTicket, type StaffQrPurpose } from '../../services/api';
import { SOCKET_URL } from '../../config/api';

type Props = {
  machineCode: string;
  purpose: StaffQrPurpose;
  /** Guest QR vẫn hiện trên máy; card này chỉ cho user/staff trên web. */
  compact?: boolean;
  qrSize?: number;
};

const StaffUnlimitedQrCard: React.FC<Props> = ({
  machineCode,
  purpose,
  compact = false,
  qrSize,
}) => {
  const mc = (machineCode || '').trim().toUpperCase();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const mintGenRef = useRef(0);
  const socketRef = useRef<Socket | null>(null);

  const size = qrSize ?? (compact ? 148 : 200);
  const isPhotobooth = purpose === 'photobooth-simple';
  const title = isPhotobooth ? 'QR nhân viên (Photobooth)' : 'QR nhân viên (Mobile Upload)';
  const hint = isPhotobooth
    ? 'Không giới hạn lượt · không cần mã 5 số · mã đổi sau mỗi lần quét'
    : 'Không giới hạn lượt · vẫn chiếm slot máy · mã đổi sau mỗi lần quét';

  const mint = useCallback(async () => {
    if (!mc) return;
    const gen = ++mintGenRef.current;
    setLoading(true);
    setError('');
    try {
      const data = await mintStaffQrTicket(mc, purpose);
      if (gen !== mintGenRef.current) return;
      if (!data.success || !data.url) {
        setError('Không tạo được QR nhân viên');
        setUrl('');
        return;
      }
      setUrl(data.url);
    } catch (err: any) {
      if (gen !== mintGenRef.current) return;
      setError(err?.response?.data?.message || err?.message || 'Không tạo được QR nhân viên');
      setUrl('');
    } finally {
      if (gen === mintGenRef.current) setLoading(false);
    }
  }, [mc, purpose]);

  useEffect(() => {
    void mint();
  }, [mint]);

  useEffect(() => {
    if (!mc) return;
    const socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    const join = () => {
      socket.emit('join-machine-room', mc);
    };
    socket.on('connect', join);
    join();

    socket.on('staff-qr-ticket-consumed', (data: { machineCode?: string; purpose?: string }) => {
      const code = String(data?.machineCode || '').trim().toUpperCase();
      const p = String(data?.purpose || '').trim().toLowerCase();
      if (code && code !== mc) return;
      if (p && p !== purpose) return;
      void mint();
    });

    // Mobile upload staff URL embeds randomCode — remint when guest code rotates.
    if (purpose === 'mobile-upload') {
      socket.on('random-code-updated', (data: { machineCode?: string; machineId?: string }) => {
        const code = String(data?.machineCode || data?.machineId || '')
          .trim()
          .toUpperCase();
        if (code && code !== mc) return;
        void mint();
      });
    }

    return () => {
      try {
        socket.emit('leave-machine-room', mc);
      } catch {
        /* ignore */
      }
      socket.disconnect();
      socketRef.current = null;
    };
  }, [mc, purpose, mint]);

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        borderColor: (th) => alpha(th.palette.secondary.main, 0.45),
        bgcolor: (th) => alpha(th.palette.secondary.main, 0.03),
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
          <Box>
            <Typography variant="body2" fontWeight={800} color="secondary.dark">
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {hint}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => void mint()} disabled={loading} aria-label="Làm mới QR">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void mint()}>
                Thử lại
              </Button>
            }
          >
            {error}
          </Alert>
        ) : (
          <>
            <Box display="flex" justifyContent="center" my={1.5}>
              <Box
                onClick={() => window.open(url, '_blank')}
                sx={{
                  p: 1.25,
                  border: '2px solid',
                  borderColor: 'secondary.main',
                  borderRadius: 2,
                  bgcolor: 'white',
                  cursor: 'pointer',
                  '&:active': { opacity: 0.85 },
                }}
              >
                <QRCodeSVG value={url} size={size} includeMargin />
              </Box>
            </Box>
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={copied ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                onClick={() => void handleCopy()}
              >
                Sao chép link
              </Button>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StaffUnlimitedQrCard;
